// =============================================================================
// makeSliceHook — single-sources the useSyncExternalStore boilerplate for a page
// slice. Given a slice key, returns a typed useXxxSlice(selector) hook. Keeps every
// slice's subscribe/getSnapshot/getServerSnapshot wiring identical so none can drift
// (e.g. a new slice forgetting the getServerSnapshot argument).
//
// Selectors MUST return referentially-stable values (a primitive or an existing
// reference), never a freshly-constructed object/array — useSyncExternalStore compares
// snapshots with Object.is, so a new reference each call loops forever. Derive/clone in
// a useMemo at the call site instead.
// =============================================================================
import { useSyncExternalStore } from 'react'
import { getState, getServerSnapshot, subscribe, type PageState } from './pageStore'

export function makeSliceHook<K extends keyof PageState>(key: K) {
  return function useSlice<T>(selector: (s: PageState[K]) => T): T {
    return useSyncExternalStore(
      subscribe,
      () => selector(getState()[key]),
      () => selector(getServerSnapshot()[key]),
    )
  }
}
